/*@flow*/
import React, {
  useEffect,
  useState,
  createContext,
  useReducer,
  Fragment,
  useContext,
} from 'react';
import lensPath from 'ramda/src/lensPath';
import set from 'ramda/src/set';
import hash from 'lib/hash';
import { fromNullable as maybe } from 'folktale/maybe';
import weekdays from 'lib/weekdays';
import * as I from 'immutable';
import setIn from 'lib/setIn';
import EmptyArray from 'lib/EmptyArray';
import cx from 'classnames';

type IntervalT = {
  opening: string,
  closing: string,
};

type Day = {
  day: number,
  name?: string,
  intervals: IntervalT[],
};

type Props = {
  days: Day[],
  onChange?: (days: Day[]) => void,
};

type State = $ReadOnly<{ days: Day[] }>;
type Action = { type: string, payload?: any };

const range = (start, end) =>
  Array.from(new Array(Math.round(end + 1 - start)).keys()).map(
    (v) => v + start
  );
const pad = (v) => v.padStart(2, '0');
const s = { borderStyle: 'none' };
const nonDigitsRx = /[^:\d]+/;
const onlyDigitsRx = /[:\d]+/;
const hours = range(1, 12)
  .map((v) => String(v))
  .map(pad);
const minutes = range(0, 3)
  .map((v) => String(v * 15))
  .map(pad);

const initState = { days: [] };
type CTX = {
  state: State,
  dispatch: (Action) => void,
};
const HoursContext = createContext<CTX>({});

const mkState: I.RecordFactory<State> = I.Record(initState, 'State');

const reducer = (state: I.RecordOf<State>, action: Action) => {
  const { type, payload } = action;
  let newState = state;

  switch (action.type) {
    case 'SET_RECORD':
      if (payload) {
        newState = setIn(newState)(payload);
      }
      break;
    default:
      throw new Error('Unrecognized action `' + type + '`');
  }
  return newState;
};

const commands = (ctx) => {
  const { days } = ctx.state;

  const mkInterval = (
    dayIndex,
    defaultInt = { opening: '9:00AM', closing: '6:00PM' }
  ) =>
    maybe(days[dayIndex])
      .map((d) => d.intervals[0] || defaultInt)
      .getOrElse(defaultInt);

  const mkDay = (dayIndex, int = mkInterval(dayIndex)) => ({
    day: dayIndex,
    intervals: [int],
  });

  return {
    setDays(ds: Day[]) {
      commands(ctx).set('days', ds);
    },
    removeDay(i: number) {
      const ds = [...days];
      ds.splice(i, 1);
      commands(ctx).setDays(ds);
    },
    removeInterval(dayIndex: number, i: number) {
      const ds = [...days];
      const day = { ...ds[dayIndex] };
      day.intervals.splice(i, 1);
      ds[dayIndex] = day;
      commands(ctx).setDays(ds);
    },
    coldStart() {
      let ds = [];
      range(0, 6).forEach((k) => ds.push(mkDay(k)));
      commands(ctx).setDays(ds);
    },
    reset() {
      const ixs = days.map((d) => parseInt(d.day, 10));
      let ds = [];
      range(0, 6).forEach((i) => {
        if (ixs.includes(i)) {
          ds.push(days[ixs.indexOf(i)]);
        } else {
          ds.push(mkDay(i));
        }
      });
      commands(ctx).setDays(ds);
    },
    toggleAllDay(dayIndex: number, checked: boolean) {
      const ds = [...days];
      const d = { ...ds[dayIndex] };

      if (checked) {
        d.intervals = [];
      } else {
        d.intervals = [mkInterval(dayIndex)].filter(Boolean);
      }

      ds[dayIndex] = d;

      commands(ctx).setDays([...ds]);
    },
    addInterval(dayIndex: number) {
      const ds = [...days];
      const day = { ...ds[dayIndex] };
      day.intervals.push(mkInterval(day.day));
      ds[dayIndex] = day;
      commands(ctx).setDays(ds);
    },
    removeAll() {
      commands(ctx).setDays([]);
    },
    set(path, value) {
      ctx.dispatch({
        type: 'SET_RECORD',
        payload: { path, value },
      });
    },
  };
};

const getDigits = (value) =>
  value
    .replace(nonDigitsRx, '')
    .split(':')
    .map(pad);
const getInitAmPm = (value) => () => value.replace(onlyDigitsRx, '') || 'AM';
const getInitHours = (digits) => () => digits[0] || '09';
const getInitMinutes = (digits) => () => digits[1] || '00';

const Button = ({
  children,
  onClick,
  className = '',
  ...rest
}: {
  children: React$Node,
  onClick: Function,
  className?: string,
}) => (
  <button
    {...rest}
    onClick={(e) => {
      e.preventDefault();
      onClick && onClick(e);
    }}
    className={cx('p-0', className)}
  >
    {children}
  </button>
);

type TimeBoundaryProps = {
  value: string,
  name: string,
  dayIndex: number,
  intervalIndex: number,
};

const isOpenAllDay = (day) => {
  if (EmptyArray.is(day.intervals)) return true;
  const MIDNIGHT = '12:00AM';
  return day.intervals.every(
    (v) => v.opening === MIDNIGHT && v.closing === MIDNIGHT
  );
};

const TimeBoundary = (props: TimeBoundaryProps) => {
  const { name, value, dayIndex, intervalIndex } = props;
  const ctx = useContext(HoursContext);
  const { days } = ctx.state;
  const digits = getDigits(value);
  const [hour, setHour] = useState(getInitHours(digits));
  const [minute, setMinute] = useState(getInitMinutes(digits));
  const [ampm, setMeridian] = useState(getInitAmPm(value));
  const ampms = ['AM', 'PM'];
  const day = days[dayIndex];
  const allDay = isOpenAllDay(day);

  useEffect(() => {
    // Set the days for the interval. (Using a lens to avoid mutation.)
    const path = lensPath([dayIndex, 'intervals', intervalIndex, name]);
    let ds = set(path, `${hour}:${minute}${ampm}`, days);
    commands(ctx).setDays(ds);
  }, [hour, minute, ampm]);

  const testIdBase = `TimeBoundary-${dayIndex}-${intervalIndex}-${name}`;

  return (
    <span>
      <select
        style={s}
        onChange={(e) => {
          const v = e.target.value;
          setHour(v);
        }}
        disabled={allDay}
        value={hour}
        data-testid={`${testIdBase}-hour`}
      >
        {hours.map((v, i) => (
          <option value={v} key={i}>
            {v}
          </option>
        ))}
      </select>
      <select
        style={s}
        onChange={(e) => {
          const v = e.target.value;
          setMinute(v);
        }}
        disabled={allDay}
        value={minute}
        data-testid={`${testIdBase}-minute`}
      >
        {minutes.map((v, i) => (
          <option value={v} key={i}>
            {v}
          </option>
        ))}
      </select>
      <select
        style={s}
        onChange={(e) => {
          const v = e.target.value;
          setMeridian(v);
        }}
        disabled={allDay}
        value={ampm}
        data-testid={`${testIdBase}-meridian`}
      >
        {ampms.map((v, i) => (
          <option value={v} key={i}>
            {v}
          </option>
        ))}
      </select>
    </span>
  );
};

const Days = () => {
  const ctx = useContext(HoursContext);
  const { days } = ctx.state;
  if (days.length === 0) return null;

  const intervalFactory = (dayIndex) => (item, intervalIndex) => {
    if (!item) return null;
    const testid = `Interval-${dayIndex}-${intervalIndex}`;
    return (
      <div
        data-testid={testid}
        className="mb-2"
        key={`${dayIndex}-${intervalIndex}`}
      >
        <Fragment>
          <TimeBoundary
            name="opening"
            value={item.opening}
            dayIndex={dayIndex}
            intervalIndex={intervalIndex}
          />{' '}
          -{' '}
          <TimeBoundary
            name="closing"
            value={item.closing}
            dayIndex={dayIndex}
            intervalIndex={intervalIndex}
          />{' '}
        </Fragment>
        {intervalIndex > 0 && (
          <Button
            data-testid={`Remove${testid}`}
            onClick={() => {
              commands(ctx).removeInterval(dayIndex, intervalIndex);
            }}
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </div>
    );
  };

  const getDayName = (day) => weekdays[day.day];

  const mkDay = (day, dayIndex) => {
    const allDay = EmptyArray.is(day.intervals);
    const dayName = getDayName(day);
    return (
      <div key={dayIndex} className="d-flex">
        <span className="d-inline-block" style={{ minWidth: 120 }}>
          <Button
            data-testid={`Remove${dayName}`}
            onClick={() => commands(ctx).removeDay(dayIndex)}
          >
            <i className="fa fa-times" />
          </Button>{' '}
          <span data-testid="DayName">{dayName}</span>
        </span>
        <div>
          {!allDay && (
            <div>
              {I.getIn(day, ['intervals'], []).map(intervalFactory(dayIndex))}
            </div>
          )}
          {!allDay && (
            <Button
              data-testid={`AddInterval-${dayIndex}`}
              className="text-primary mr-2 pl-0"
              onClick={() => commands(ctx).addInterval(dayIndex)}
            >
              Add another interval
            </Button>
          )}
          <label className="user-select-none">
            <input
              type="checkbox"
              onChange={(e) =>
                commands(ctx).toggleAllDay(dayIndex, e.target.checked)
              }
              checked={allDay}
              data-testid={`Toggle24Hours-${dayIndex}`}
            />{' '}
            Open 24 hours
          </label>
        </div>
      </div>
    );
  };

  const byDay = (a, b) => (a.day < b.day ? -1 : 1);

  return days.sort(byDay).map(mkDay);
};

const Prompt = () => {
  const ctx = useContext(HoursContext);
  const { days } = ctx.state;
  const [isLoading, setLoading] = useState(false);

  if (days.length === 0) {
    return (
      <Button
        className="btn btn-info text-white p-4 btn-normalcase text-white"
        onClick={() => {
          setLoading(true);
          setTimeout(() => {
            commands(ctx).coldStart();
          }, 20);
          setTimeout(() => setLoading(false), 1000);
        }}
      >
        {isLoading ? (
          <span>Please wait...</span>
        ) : (
          <Fragment>
            <i className="fa fa-clock" /> Add hours
          </Fragment>
        )}
      </Button>
    );
  }

  const lessThanAWeek = days.length < 7;
  const style = lessThanAWeek
    ? {}
    : { color: '#ccc', textDecoration: 'none', cursor: 'auto' };

  return (
    <Fragment>
      <Button
        className="text-primary"
        onClick={() => commands(ctx).removeAll()}
      >
        Remove all
      </Button>{' '}
      -{' '}
      <Button
        style={style}
        className="text-primary"
        disabled={!lessThanAWeek}
        onClick={() => lessThanAWeek && commands(ctx).reset()}
      >
        Reset
      </Button>
    </Fragment>
  );
};

const Component = (props: Props) => {
  const [state, dispatch] = useReducer(reducer, mkState({ days: props.days }));
  const ctx = { state, dispatch };
  const { days } = ctx.state;
  const [isPristine, setPristine] = useState(true);

  useEffect(() => {
    if (isPristine) return;
    const { onChange } = props;
    // Only call the callback if the state is not pristine.
    onChange && onChange(days);
  }, [hash(days)]);

  useEffect(() => {
    isPristine && setPristine(false);
  }, []);

  useEffect(() => {
    if (hash(days) === hash(props.days)) return;
    commands(ctx).setDays(props.days);
  }, [hash(props.days)]);

  return (
    <HoursContext.Provider value={ctx}>
      <div className="Hours">
        <Days />
        <Prompt />
      </div>
    </HoursContext.Provider>
  );
};

// Usage:
//
//  const days = [{ day: 0, intevals: [{ opening: '9AM', closing: '6PM' }] }];
//
//  <Hours days={days} onChange={() => {}} />
//
export default Component;
